import { useEffect } from 'react';
import type { SidePanelView } from '@/components/desktop-side-panel';
import type { DesktopShellStore } from '@/lib/desktop-shell/store';
import { isMacPlatform, type KeyboardShortcut, matchesShortcut } from '@/lib/platform';
import {
  currentShortcut,
  panelShortcutId,
  readShortcutOverrides,
  shortcutAllowedFrom,
} from '@/lib/settings-page/keyboard-shortcuts';
import { showPanelView } from '@/lib/side-panel/panel-tabs';
import { SIDE_PANEL_ITEMS } from '@/lib/side-panel/side-panel-items';

type ShortcutEvent = Parameters<typeof matchesShortcut>[0];

type ShortcutPanel = {
  view: SidePanelView;
  session?: { shortcut?: KeyboardShortcut | undefined } | undefined;
};

/**
 * Maps a keyboard event to the side-panel view it should open, or null when the
 * chord is not a panel shortcut: a contributed panel's, else a core item's
 * (`SIDE_PANEL_ITEMS`), with the platform's primary modifier. Pure so the
 * chord table is unit-testable.
 */
export function resolveSidePanelShortcut(
  event: ShortcutEvent,
  panels: readonly ShortcutPanel[] = [],
  mac = isMacPlatform(),
): SidePanelView | null {
  const contributed = panels.find(
    (panel) =>
      panel.session?.shortcut !== undefined && matchesShortcut(event, panel.session.shortcut, mac),
  );
  if (contributed !== undefined) return contributed.view;
  // The core's tools open with the keys Settings › Keyboard shortcuts gives them.
  const overrides = readShortcutOverrides();
  const item = SIDE_PANEL_ITEMS.find((candidate) => {
    if (!('shortcut' in candidate) || candidate.shortcut === undefined) return false;
    const keys = currentShortcut(panelShortcutId(candidate.id), overrides) ?? candidate.shortcut;
    return matchesShortcut(event, keys, mac);
  });
  return item?.id ?? null;
}

/** Global keyboard shortcuts that open a side-panel view. */
export function useSidePanelShortcuts(
  store: DesktopShellStore,
  panels: readonly ShortcutPanel[] = [],
): void {
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent): void {
      if (!shortcutAllowedFrom(event)) return;
      const view = resolveSidePanelShortcut(event, panels);
      if (view === null) return;
      event.preventDefault();
      const { setMessageReviewDiff, setPanelTabs, setSidePanelOpen } = store.getState();
      if (view === 'review') setMessageReviewDiff(null);
      setPanelTabs((tabs) => showPanelView(tabs, view));
      setSidePanelOpen(true);
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [panels, store]);
}
